import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  alpha
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Assignment as TaskIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  NotificationImportant as AlertsIcon,
  AccessTime as OverdueIcon
} from '@mui/icons-material';
import { getWorkflowTasks } from '../../services/encounterService';
import { subscribeToAlerts } from '../../services/alertService';
import { useAuth } from '../../context/AuthContext';

const AlertsTab = React.memo(({ encounter, patient }) => {
  const theme = useTheme();
  const [alerts, setAlerts] = useState([]);
  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { user } = useAuth();

  // Memoize user ID to prevent unnecessary re-renders
  const userId = useMemo(() => user?.uid, [user?.uid]);

  // Fetch workflow tasks to check for overdue items
  const fetchWorkflowTasks = useCallback(async (showRefreshing = false) => {
    if (!encounter?.id || !user) return;
    
    if (showRefreshing) setRefreshing(true);
    
    try {
      const token = await user.getIdToken();
      // Request all tasks without pagination for alert checking
      const options = {
        pageSize: 1000, // Large page size to get all tasks
        status: 'all' // Get all statuses to check for overdue
      };
      const response = await getWorkflowTasks(encounter.id, token, options);
      
      // Handle both paginated and non-paginated responses
      const tasks = response.data && Array.isArray(response.data) ? response.data : 
                   Array.isArray(response) ? response : [];
      
      setWorkflowTasks(tasks);
    } catch (error) {
      console.error('Error fetching workflow tasks:', error);
    } finally {
      setRefreshing(false);
    }
  }, [encounter?.id, user]);

  // Subscribe to alerts for this patient/encounter
  useEffect(() => {
    console.log('🔍 AlertsTab useEffect triggered with dependencies:', {
      patientId: patient?.id,
      encounterId: encounter?.id,
      userId: userId,
      timestamp: new Date().toISOString()
    });

    if (!patient?.id && !encounter?.id) {
      console.log('❌ AlertsTab: Missing required IDs, skipping listener setup');
      return;
    }

    console.log('📡 AlertsTab: Setting up alerts subscription...');
    
    // Subscribe to alerts using service layer
    const cleanup = subscribeToAlerts(
      { patientId: patient?.id, encounterId: encounter?.id, userId },
      (alertsData) => {
        console.log('📨 AlertsTab: Alerts update received:', {
          timestamp: new Date().toISOString(),
          alertsCount: alertsData.length,
          alerts: alertsData.map(a => ({ id: a.id, title: a.title, type: a.type }))
        });
        
        setAlerts(alertsData);
        setLoading(false);
      }
    );

    console.log('✅ AlertsTab: Subscription setup complete, returning cleanup function');
    return () => {
      console.log('🧹 AlertsTab: Cleaning up alerts subscription');
      cleanup();
    };
  }, [patient?.id, encounter?.id, userId]);

  // Fetch workflow tasks on component mount and when encounter changes
  useEffect(() => {
    fetchWorkflowTasks();
  }, [fetchWorkflowTasks]);

  // Calculate overdue and critical tasks
  const taskAlerts = useMemo(() => {
    const now = new Date();
    const overdueTasks = workflowTasks.filter(task => {
      if (task.status === 'completed' || task.status === 'cancelled') return false;
      
      let dueDate = null;
      if (task.dueAt) {
        if (task.dueAt.seconds) {
          dueDate = new Date(task.dueAt.seconds * 1000);
        } else if (task.dueAt.toDate) {
          dueDate = task.dueAt.toDate();
        } else {
          dueDate = new Date(task.dueAt);
        }
      }
      
      return dueDate && dueDate < now;
    });

    return overdueTasks.map(task => ({
      id: `task-${task.stepId}`,
      type: 'OVERDUE_TASK',
      severity: 'error',
      title: 'Overdue Workflow Task',
      message: `${task.stepMetadata?.title || 'Task'} is overdue`,
      createdAt: task.dueAt,
      metadata: {
        taskId: task.stepId,
        taskTitle: task.stepMetadata?.title,
        assignedTo: task.assignedTo,
        dueAt: task.dueAt
      }
    }));
  }, [workflowTasks]);

  // Combine all alerts
  const allAlerts = useMemo(() => {
    return [...alerts, ...taskAlerts].sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
      const bTime = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
      return bTime - aTime;
    });
  }, [alerts, taskAlerts]);

  // Group alerts by severity
  const alertsByType = useMemo(() => {
    const grouped = {
      critical: [],
      warning: [],
      info: []
    };

    allAlerts.forEach(alert => {
      if (alert.type === 'OVERDUE_TASK' || alert.severity === 'error' || alert.severity === 'critical') {
        grouped.critical.push(alert);
      } else if (alert.severity === 'warning') {
        grouped.warning.push(alert);
      } else {
        grouped.info.push(alert);
      }
    });

    return grouped;
  }, [allAlerts]);

  const getAlertIcon = (type, severity) => {
    if (type === 'OVERDUE_TASK') return <OverdueIcon color="error" />;
    if (severity === 'error' || severity === 'critical') return <ErrorIcon color="error" />;
    if (severity === 'warning') return <WarningIcon color="warning" />;
    return <AlertsIcon color="info" />;
  };

  const getAlertColor = (type, severity) => {
    if (type === 'OVERDUE_TASK') return 'error';
    if (severity === 'error' || severity === 'critical') return 'error';
    if (severity === 'warning') return 'warning';
    return 'info';
  };

  const formatAlertDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ ml: 2 }}>Loading alerts...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with refresh button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Patient Alerts & Notifications
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label={`${allAlerts.length} Total`} 
            color="primary" 
            size="small"
            variant="outlined"
          />
          {alertsByType.critical.length > 0 && (
            <Chip 
              label={`${alertsByType.critical.length} Critical`} 
              color="error" 
              size="small"
            />
          )}
          <Tooltip title="Refresh alerts">
            <IconButton 
              onClick={() => fetchWorkflowTasks(true)} 
              disabled={refreshing}
              size="small"
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {allAlerts.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No Active Alerts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All workflow tasks are on track and no critical alerts are present.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {/* Critical Alerts */}
          {alertsByType.critical.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ border: `1px solid ${theme.palette.error.main}`, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                      Critical Alerts ({alertsByType.critical.length})
                    </Typography>
                  </Box>
                  <List dense>
                    {alertsByType.critical.map((alert, index) => (
                      <React.Fragment key={alert.id}>
                        <ListItem sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getAlertIcon(alert.type, alert.severity)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {alert.title}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.message}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatAlertDate(alert.createdAt)}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip 
                            label={alert.type === 'OVERDUE_TASK' ? 'Overdue' : 'Critical'} 
                            color="error" 
                            size="small"
                          />
                        </ListItem>
                        {index < alertsByType.critical.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Warning Alerts */}
          {alertsByType.warning.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ border: `1px solid ${theme.palette.warning.main}`, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      Warning Alerts ({alertsByType.warning.length})
                    </Typography>
                  </Box>
                  <List dense>
                    {alertsByType.warning.map((alert, index) => (
                      <React.Fragment key={alert.id}>
                        <ListItem sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getAlertIcon(alert.type, alert.severity)}
                          </ListItemIcon>
                          <ListItemText
                            primary={alert.title}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.message}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatAlertDate(alert.createdAt)}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip label="Warning" color="warning" size="small" />
                        </ListItem>
                        {index < alertsByType.warning.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Info Alerts */}
          {alertsByType.info.length > 0 && (
            <Grid item xs={12}>
              <Card sx={{ border: `1px solid ${theme.palette.info.main}`, borderRadius: 2 }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AlertsIcon color="info" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                      Information Alerts ({alertsByType.info.length})
                    </Typography>
                  </Box>
                  <List dense>
                    {alertsByType.info.map((alert, index) => (
                      <React.Fragment key={alert.id}>
                        <ListItem sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getAlertIcon(alert.type, alert.severity)}
                          </ListItemIcon>
                          <ListItemText
                            primary={alert.title}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.message}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatAlertDate(alert.createdAt)}
                                </Typography>
                              </Box>
                            }
                          />
                          <Chip label="Info" color="info" size="small" />
                        </ListItem>
                        {index < alertsByType.info.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
});

export default AlertsTab; 